import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useResizeRef from 'use-resize-observer/polyfilled';
import {Box, IconButton} from '@mui/material';
import {DateTime} from 'luxon';
import {FixedSizeList} from 'react-window';
import {connect} from 'react-redux';
import {css} from '@emotion/css';

import * as intervals from '@explorer/util/intervals';
import TimelinePeriod from './timeline-period.jsx';
import {Line} from './line.jsx';
import {MODALS, TILE_MODE, TOUR_STAGES} from '@explorer/constants';
import {Marker} from './marker.jsx';
import {addResultToOrder, addResultToView} from '@explorer/actions/results';
import {
  calcBasemapPeriods,
  calcDailyPeriods,
  getLabel,
  getPeriodWidth,
  isBasemap,
} from './util.jsx';
import {getMosaics, getSelectedMosaic} from '@explorer/selectors/mosaics';
import {getSelectedSeries} from '@explorer/selectors/series';
import {
  getSelectedViewResults,
  selectFilteredResultsList,
} from '@explorer/selectors/results';
import {selectMosaic} from '@explorer/actions/mosaics';
import {showModal} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';

// This does not follow current patterns of using `styled`
// the kebab-case styles were not being applied unless we
// created a className or use a css file
const timelineClass = css`
  flex: 1;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Timeline = props => {
  const {
    selectMosaic,
    showModal,
    results,
    selectedSeries,
    selectedMosaic,
    tileMode,
    mosaics,
    selectedResults,
    addResultToView,
    addResultToOrder,
  } = props;
  const {
    ref: parentRef,
    width: parentWidth,
    height: parentHeight,
  } = useResizeRef();
  const listRef = useRef();
  const listBoxRef = useRef();
  const outerRef = useRef();
  const innerRef = useRef();
  const debounceTimeout = useRef();

  const periodWidth = getPeriodWidth(
    tileMode === TILE_MODE.RESULTS ? intervals.DAY : selectedSeries?.interval
  );

  const [navLeftEnabled, setNavLeftEnabled] = useState(true);
  const [navRightEnabled, setNavRightEnabled] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const periods = useMemo(() => {
    let periods = [];
    // check for basemaps...
    if (tileMode === TILE_MODE.BASEMAPS && selectedSeries) {
      periods = calcBasemapPeriods(selectedSeries, mosaics, selectedMosaic);
    } else {
      // ensure an array is always returned.
      if (results && selectedResults) {
        periods = calcDailyPeriods(results, selectedResults);
      }
    }
    // this adds a scrollable "dead pedal"
    //  so that the year label at the start of the list is not cut-off
    if (periods.length > 0) {
      periods.unshift(null);
    }
    return periods;
  }, [
    tileMode,
    selectedSeries,
    mosaics,
    selectedMosaic,
    results,
    selectedResults,
  ]);

  useEffect(() => {
    let maxSelected = -1;
    for (let i = periods.length - 1; i >= 0; i--) {
      if (periods[i] && periods[i].selected) {
        maxSelected = i;
        break;
      }
    }
    if (maxSelected > 0) {
      listRef.current.scrollToItem(maxSelected);
    } else {
      listRef.current.scrollToItem(periods.length - 1);
    }
  }, [listRef, periods]);

  const getNextLeft = useCallback(
    (diff, scrollLeft = null) => {
      const left = scrollLeft ? scrollLeft : outerRef.current.scrollLeft;
      return Math.min(
        Math.max(0, left + diff),
        outerRef.current.scrollWidth - outerRef.current.offsetWidth
      );
    },
    [outerRef]
  );

  const paginate = useCallback(
    direction => {
      // there may be a smarter way of calculating page size.
      const diff = periodWidth * 5 * direction;
      listRef.current.scrollTo(getNextLeft(diff));
    },
    [listRef, periodWidth, getNextLeft]
  );

  const onPeriodClick = useCallback(
    period => {
      if (!isDragging) {
        const isDaily = period.interval === intervals.DAY;

        if (period.hasResult && isBasemap(period, selectedSeries?.interval)) {
          selectMosaic(period.id);
          trackEvent('Mosaic changed', {
            keypress: false,
            mosaic: period.name,
          });
        } else if (period.hasResult && isDaily) {
          // find the results where the acquired date matches the
          // selected timeline date then select them.
          if (results.length > 0) {
            const selectedResults = [];
            for (let i = 0; i < results.length; i++) {
              const result = results[i];
              const iso = DateTime.fromISO(result.properties.acquired, {
                zone: 'utc',
              })
                .startOf('day')
                .toISODate();
              const firstAcquired = DateTime.fromISO(period.first_acquired, {
                zone: 'utc',
              }).toISODate();
              if (iso < firstAcquired) {
                break;
              }
              if (iso === firstAcquired) {
                selectedResults.push(result.id);
              }
            }

            if (selectedResults.length) {
              addResultToView({
                resultId: selectedResults[0],
                reset: true,
              });
              addResultToOrder({
                resultId: selectedResults[0],
                reset: true,
              });
            }
          }
        } else if (period.available) {
          if (isDaily) {
            showModal(MODALS.SCENES_NO_ACCESS);
          } else {
            showModal(MODALS.BASEMAPS_NO_ACCESS);
          }
        }
      }
    },
    [
      isDragging,
      selectedSeries?.interval,
      selectMosaic,
      results,
      addResultToView,
      addResultToOrder,
      showModal,
    ]
  );

  const Period = useCallback(
    ({index, style}) => {
      const period = periods[index];
      if (!period) {
        // if this is the dead pedal, or simply undefined
        //  return an empty box.
        return <Box />;
      }

      const label = getLabel(period);
      return (
        <Box style={style}>
          <TimelinePeriod
            key={label}
            label={label}
            onClick={() => onPeriodClick(period)}
            period={period}
          />
          <Marker
            index={index}
            isDaily={period.interval === intervals.DAY}
            period={period}
          />
        </Box>
      );
    },
    [periods, onPeriodClick]
  );

  const mouseMover = useCallback(
    downEvt => {
      const startX = downEvt.clientX;
      const seedX = outerRef.current.scrollLeft;
      const mover = moveEvt => {
        if (!isDragging) {
          setIsDragging(true);
        }
        const diff = startX - moveEvt.clientX;
        listRef.current.scrollTo(getNextLeft(diff, seedX));
      };
      const upper = () => {
        window.removeEventListener('mouseup', upper);
        window.removeEventListener('mousemove', mover);
        // keeps the click event from triggering when a period is being dragged.
        setTimeout(() => {
          setIsDragging(false);
        }, 100);
      };

      window.addEventListener('mousemove', mover);
      window.addEventListener('mouseup', upper);
    },
    [listRef, outerRef, isDragging, setIsDragging, getNextLeft]
  );

  // the "style={{overflowY: 'hidden'}}" is required to properly
  //  override the scroll behaviour. react-window sets the overflow
  //  property with style={} and therefore class overrides won't work.
  return (
    <React.Fragment>
      <Box
        ref={parentRef}
        sx={{
          overflow: 'hidden',
          fontSize: 'caption.fontSize',
          color: 'common.white',
          userSelect: 'none',
          display: 'flex',
          height: theme => theme.mixins.timeline.height,
          position: 'relative',
          flex: 1,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        data-tour={TOUR_STAGES.TIMELINE}
      >
        <IconButton
          size="small"
          color="inherit"
          disabled={!navLeftEnabled || periods.length === 0}
          onClick={() => paginate(-1)}
          sx={{
            bottom: 8 * 0.75,
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <Box
          ref={listBoxRef}
          onMouseDown={mouseMover}
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          <Line />
          <FixedSizeList
            ref={listRef}
            outerRef={outerRef}
            innerRef={innerRef}
            width={parentWidth ? parentWidth - 64 : 10}
            height={parentHeight ? parentHeight : 10}
            itemSize={periodWidth}
            itemCount={periods.length}
            layout="horizontal"
            style={{
              overflowY: 'hidden',
            }}
            className={timelineClass}
            onScroll={({scrollOffset}) => {
              if (!outerRef.current || !scrollOffset) {
                return;
              }
              if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
              }
              debounceTimeout.current = setTimeout(() => {
                const rightBound = outerRef.current
                  ? outerRef.current.scrollWidth - outerRef.current.offsetWidth
                  : 0;
                setNavLeftEnabled(scrollOffset > 0);
                setNavRightEnabled(scrollOffset < rightBound);
              }, 25);
            }}
            onItemsRendered={() => {
              if (innerRef.current.offsetWidth < outerRef.current.offsetWidth) {
                innerRef.current.style.position = 'absolute';
                innerRef.current.style.right = 0;
              } else {
                innerRef.current.style.position = '';
                innerRef.current.style.right = 'unset';
              }
            }}
          >
            {Period}
          </FixedSizeList>
        </Box>
        <IconButton
          size="small"
          color="inherit"
          disabled={!navRightEnabled || periods.length === 0}
          onClick={() => paginate(1)}
          sx={{
            bottom: 8 * 0.75,
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    </React.Fragment>
  );
};

Timeline.propTypes = {
  addResultToOrder: PropTypes.func,
  addResultToView: PropTypes.func,
  mosaics: PropTypes.array,
  results: PropTypes.array,
  selectMosaic: PropTypes.func,
  selectedMosaic: PropTypes.object,
  selectedResults: PropTypes.array,
  selectedSeries: PropTypes.object,
  showModal: PropTypes.func,
  tileMode: PropTypes.string,
};

const mapStateToProps = state => ({
  mosaics: getMosaics(state),
  results: selectFilteredResultsList(state),
  selectedMosaic: getSelectedMosaic(state),
  selectedResults: getSelectedViewResults(state),
  selectedSeries: getSelectedSeries(state),
  tileMode: state.tiles.mode,
});

const mapDispatchProps = {
  addResultToOrder,
  addResultToView,
  selectMosaic,
  showModal,
};

export default connect(mapStateToProps, mapDispatchProps)(Timeline);
